import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { reportError } from '../../../helpers/reportError';
import { ConversationMessageWithStatus } from '../../../types/conversation.types';
import { replaceChatMessage } from '../chatCache';
import {
  patchConversationMessage,
  removeConversationMessage,
} from '../conversationTransport';

/**
 * Editing and deleting a message you already sent.
 *
 * Both write the server's answer straight into the cache rather than
 * invalidating: the response IS the new row, and refetching an infinite query
 * would pull every loaded page again. The socket echo that follows is a no-op,
 * since `replaceChatMessage` matches on id.
 *
 * Sending lives in `useSendChatMessage` instead - it is optimistic, retryable
 * and idempotency-keyed, none of which these two are.
 */
export const useMessageMutations = (conversationId: number | string | null) => {
  const queryClient = useQueryClient();

  /** False when the edit did not land, so the composer stays in edit mode. */
  const editMessage = useCallback(
    async (messageId: number, body: string) => {
      if (conversationId === null) return false;
      try {
        const { data } = await patchConversationMessage(
          conversationId,
          messageId,
          body
        );
        replaceChatMessage(queryClient, conversationId, data.data);
        return true;
      } catch (error) {
        reportError(error);
        return false;
      }
    },
    [conversationId, queryClient]
  );

  const deleteMessage = useCallback(
    async (message: ConversationMessageWithStatus) => {
      if (conversationId === null) return;
      try {
        // Soft delete - the row keeps its slot and re-renders as "Message
        // deleted", so nothing about ordering or pagination shifts.
        const { data } = await removeConversationMessage(
          conversationId,
          message.id
        );
        replaceChatMessage(queryClient, conversationId, data.data);
      } catch (error) {
        reportError(error);
      }
    },
    [conversationId, queryClient]
  );

  return { editMessage, deleteMessage };
};
